import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  TextField,
  Typography
} from "@mui/material";

import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";

import DashboardLayout from "../layout/DashboardLayout";

import Banner1 from "../assets/Banner_1.jpeg";
import Banner2 from "../assets/Banner_2.jpeg";

const ROOMS = [
  {
    key: "small",
    title: "Small",
    subtitle: "Up to 3 people",
    desc: "Quiet, enclosed, ideal for discussions",
    features: ["Screen", "Whiteboard", "Video Call"]
  },
  {
    key: "large",
    title: "Large",
    subtitle: "Up to 10 people",
    desc: "Spacious, bright, perfect for team meetings",
    features: ["Screen", "Whiteboard", "Video Call"]
  }
];

const ADDONS = [
  { key: "tea_coffee", title: "Tea / Coffee Service", desc: "Freshly brewed beverages for your team" },
  { key: "snacks", title: "Snacks & Refreshments", desc: "Light snacks and refreshments" },
  { key: "additional_seating", title: "Additional Seating", desc: "Extra chairs for larger groups" },
  { key: "whiteboard_stationery", title: "Whiteboard / Stationery", desc: "Markers, notepads, and writing supplies" },
  { key: "video_setup", title: "Video Conferencing Setup", desc: "Professional AV setup with technical support" }
];

function toDateInputValue(d = new Date()) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export default function BookMeetingRoom() {
  const [selectedRoom, setSelectedRoom] = useState("large");

  const [date, setDate] = useState(toDateInputValue(new Date()));
  const [startTime, setStartTime] = useState("11:00");
  const [endTime, setEndTime] = useState("12:00");

  const [meetingTitle, setMeetingTitle] = useState("");
  const [attendees, setAttendees] = useState("");
  const [meetingType, setMeetingType] = useState("internal");

  const [addons, setAddons] = useState(() => {
    const init = {};
    ADDONS.forEach((a) => (init[a.key] = false));
    return init;
  });

  const banners = useMemo(
    () => [
      { id: "b1", image: Banner1 },
      { id: "b2", image: Banner2 }
    ],
    []
  );

  const room = useMemo(() => ROOMS.find((r) => r.key === selectedRoom) || ROOMS[0], [selectedRoom]);

  const selectedAddons = useMemo(
    () => Object.entries(addons).filter(([, v]) => v).map(([k]) => k),
    [addons]
  );

  const handleToggleAddon = (key) => {
    setAddons((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleConfirm = () => {
    if (!date) return alert("Please select a date.");
    if (!startTime || !endTime) return alert("Please select start and end time.");

    const payload = {
      room: selectedRoom,
      date,
      startTime,
      endTime,
      meetingTitle: meetingTitle || null,
      attendees: attendees ? Number(attendees) : null,
      meetingType,
      addons: selectedAddons
    };

    console.log("Meeting room booking payload:", payload);
    alert("Confirm Booking (hook backend/payment next).");
  };

  return (
    <DashboardLayout title="Dashboard">
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 320px" }, gap: 3 }}>
        {/* LEFT */}
        <Box sx={{ minWidth: 0 }}>
          {/* Top banner strip */}
          <Card sx={{ bgcolor: "action.selected" }}>
            <CardContent sx={{ py: 2.25 }}>
              <Typography sx={{ fontWeight: 900 }}>
                Reserve a meeting room
              </Typography>
              <Typography sx={{ mt: 0.4, fontSize: 13, color: "text.secondary" }}>
                Choose a room, time, and setup for focused meetings and conversations.
              </Typography>
            </CardContent>
          </Card>

          {/* Select a Meeting Room */}
          <Box sx={{ mt: 3 }}>
            <Box className="sectionHeader">
              <Typography className="sectionHeaderTitle">Select a Meeting Room</Typography>
              <Box className="sectionHeaderLine" />
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
              {ROOMS.map((r) => {
                const active = r.key === selectedRoom;
                return (
                  <Card
                    key={r.key}
                    onClick={() => setSelectedRoom(r.key)}
                    sx={{
                      cursor: "pointer",
                      borderColor: active ? "primary.main" : "divider",
                      "&:hover": { bgcolor: "action.hover" }
                    }}
                  >
                    <Box
                      sx={{
                        height: 130,
                        borderBottom: "1px solid",
                        borderColor: "divider",
                        bgcolor: "action.hover",
                        backgroundImage:
                          r.key === "small"
                            ? "linear-gradient(180deg, rgba(0,0,0,0.05), rgba(0,0,0,0.02))"
                            : "linear-gradient(180deg, rgba(0,0,0,0.06), rgba(0,0,0,0.02))"
                      }}
                    />
                    <CardContent>
                      <Typography sx={{ fontWeight: 900 }}>{r.title}</Typography>
                      <Typography sx={{ mt: 0.3, fontSize: 12.5, color: "text.secondary" }}>
                        {r.subtitle}
                      </Typography>
                      <Typography sx={{ mt: 0.8, fontSize: 12.5, color: "text.secondary" }}>
                        {r.desc}
                      </Typography>

                      <Box sx={{ mt: 1.2, display: "flex", gap: 2, flexWrap: "wrap" }}>
                        {r.features.map((f) => (
                          <Typography key={f} sx={{ fontSize: 12, color: "text.secondary", fontWeight: 700 }}>
                            {f}
                          </Typography>
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          </Box>

          {/* Date & Time */}
          <Box sx={{ mt: 3 }}>
            <Box className="sectionHeader">
              <Typography className="sectionHeaderTitle">Date & Time</Typography>
              <Box className="sectionHeaderLine" />
            </Box>

            <Card>
              <CardContent sx={{ py: 2.2 }}>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" }, gap: 2 }}>
                  <FormControl>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: "text.secondary", mb: 0.7 }}>
                      Date
                    </Typography>
                    <TextField
                      type="date"
                      value={date}
                      onChange={(e) => setDate(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <CalendarMonthOutlinedIcon fontSize="small" />
                          </InputAdornment>
                        )
                      }}
                      helperText="Available between 10:00 AM - 6:00 PM"
                    />
                  </FormControl>

                  <FormControl>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: "text.secondary", mb: 0.7 }}>
                      Start Time
                    </Typography>
                    <TextField
                      type="time"
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <AccessTimeOutlinedIcon fontSize="small" />
                          </InputAdornment>
                        )
                      }}
                    />
                  </FormControl>

                  <FormControl>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: "text.secondary", mb: 0.7 }}>
                      End Time
                    </Typography>
                    <TextField
                      type="time"
                      value={endTime}
                      onChange={(e) => setEndTime(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <AccessTimeOutlinedIcon fontSize="small" />
                          </InputAdornment>
                        )
                      }}
                    />
                  </FormControl>
                </Box>
              </CardContent>
            </Card>
          </Box>

          {/* Meeting Details */}
          <Box sx={{ mt: 3 }}>
            <Box className="sectionHeader">
              <Typography className="sectionHeaderTitle">Meeting Details</Typography>
              <Box className="sectionHeaderLine" />
            </Box>

            <Card>
              <CardContent sx={{ py: 2.2 }}>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "2fr 1fr 1fr" }, gap: 2 }}>
                  <FormControl>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: "text.secondary", mb: 0.7 }}>
                      Meeting Title (Optional)
                    </Typography>
                    <TextField
                      value={meetingTitle}
                      onChange={(e) => setMeetingTitle(e.target.value)}
                      placeholder="e.g. Product Strategy Review"
                    />
                  </FormControl>

                  <FormControl>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: "text.secondary", mb: 0.7 }}>
                      Number of Attendees
                    </Typography>
                    <TextField
                      value={attendees}
                      onChange={(e) => setAttendees(e.target.value)}
                      placeholder="e.g. 6"
                      inputMode="numeric"
                    />
                  </FormControl>

                  <FormControl>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: "text.secondary", mb: 0.7 }}>
                      Meeting Type (Optional)
                    </Typography>
                    <TextField
                      select
                      value={meetingType}
                      onChange={(e) => setMeetingType(e.target.value)}
                    >
                      <MenuItem value="internal">Internal</MenuItem>
                      <MenuItem value="client">Client</MenuItem>
                      <MenuItem value="workshop">Workshop</MenuItem>
                    </TextField>
                  </FormControl>
                </Box>
              </CardContent>
            </Card>
          </Box>

          {/* Add-ons */}
          <Box sx={{ mt: 3 }}>
            <Box className="sectionHeader">
              <Typography className="sectionHeaderTitle">Add-ons & Setup (Optional)</Typography>
              <Box className="sectionHeaderLine" />
            </Box>

            <Card>
              <CardContent sx={{ py: 2.2 }}>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.2 }}>
                  {ADDONS.map((a) => (
                    <Box key={a.key} sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={Boolean(addons[a.key])}
                            onChange={() => handleToggleAddon(a.key)}
                          />
                        }
                        label={
                          <Box>
                            <Typography sx={{ fontWeight: 900, fontSize: 13 }}>
                              {a.title}
                            </Typography>
                            <Typography sx={{ fontSize: 12.2, color: "text.secondary" }}>
                              {a.desc}
                            </Typography>
                          </Box>
                        }
                        sx={{ alignItems: "flex-start", m: 0 }}
                      />
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Box>

          {/* Booking Summary */}
          <Box sx={{ mt: 3 }}>
            <Card sx={{ bgcolor: "action.selected" }}>
              <CardContent sx={{ py: 2.2 }}>
                <Typography sx={{ fontWeight: 900, fontSize: 13.5 }}>
                  Booking Summary
                </Typography>

                <Box sx={{ mt: 1.5 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                    <Typography sx={{ fontSize: 12.8, color: "text.secondary", fontWeight: 700 }}>
                      Meeting Room
                    </Typography>
                    <Typography sx={{ fontSize: 12.8, fontWeight: 900 }}>
                      {room?.title || "Not selected"}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, mt: 1.1 }}>
                    <Typography sx={{ fontSize: 12.8, color: "text.secondary", fontWeight: 700 }}>
                      Date & Time
                    </Typography>
                    <Typography sx={{ fontSize: 12.8, fontWeight: 900 }}>
                      {date ? `${date} • ${startTime} - ${endTime}` : "Not selected"}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, mt: 1.1 }}>
                    <Typography sx={{ fontSize: 12.8, color: "text.secondary", fontWeight: 700 }}>
                      Add-ons
                    </Typography>
                    <Typography sx={{ fontSize: 12.8, fontWeight: 900 }}>
                      {selectedAddons.length ? `${selectedAddons.length} selected` : "None selected"}
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 1.6 }} />

                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <Button variant="contained" onClick={handleConfirm} sx={{ px: 5, py: 1.2 }}>
                      Confirm Booking
                    </Button>
                  </Box>

                  <Typography sx={{ mt: 1.1, fontSize: 12, color: "text.secondary", textAlign: "center" }}>
                    You can review details before final confirmation.
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>

        {/* RIGHT banners */}
        <Box sx={{ display: { xs: "none", lg: "flex" }, flexDirection: "column", gap: 2 }}>
          {banners.map((b) => (
            <Card key={b.id} sx={{ overflow: "hidden", bgcolor: "background.paper" }}>
              <Box sx={{ p: 1.5, bgcolor: "action.hover" }}>
                <Box
                  sx={{
                    height: 320,
                    borderRadius: 0,
                    overflow: "hidden",
                    border: "1px solid",
                    borderColor: "divider",
                    backgroundColor: "background.paper",
                    backgroundImage: `url(${b.image})`,
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "center",
                    backgroundSize: "contain"
                  }}
                />
              </Box>

              <Box sx={{ p: 1.5, pt: 1.2 }}>
                <Button fullWidth variant="outlined" sx={{ py: 1 }}>
                  Learn More
                </Button>
              </Box>
            </Card>
          ))}
        </Box>
      </Box>
    </DashboardLayout>
  );
}
